// src/components/common/AuthLayout.js
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaVideo, FaQrcode, FaDownload } from 'react-icons/fa';

const STEPS = [
  { Icon: FaVideo,    text: 'Play at an Akture-equipped court' },
  { Icon: FaQrcode,  text: 'Scan the QR code after your game' },
  { Icon: FaDownload,text: 'Purchase, download & share in HD' },
];

const AuthLayout = ({ children }) => (
  <div className="min-h-screen flex bg-white">

    {/* ── Left brand panel (desktop only) ──────────────────────────────── */}
    <aside className="hidden lg:flex lg:w-[440px] xl:w-[480px] bg-gray-950 flex-col justify-between p-10 shrink-0">

      {/* Top: logo */}
      <Link to="/" aria-label="Back to home" className="flex items-center gap-3 group w-fit">
        <img src="/1.svg" alt="Akture"
          className="h-10 w-auto object-contain opacity-90 group-hover:opacity-100 transition-opacity"
          draggable={false}/>
      </Link>

      {/* Middle: brand copy */}
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-red mb-5">
          Action Capture System
        </p>
        <h2 className="text-3xl xl:text-4xl font-black text-white leading-tight mb-4 tracking-tight">
          ALL YOUR MOMENTS,{' '}
          <span style={{
            background:'linear-gradient(95deg,#BD2026,#FF6F3C)',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent',
          }}>
            PERFECTLY PRESERVED
          </span>
        </h2>
        <p className="text-white/55 text-sm leading-relaxed mb-10">
          Your best sports memories, at your fingertips.
        </p>

        {/* 3-step mini flow */}
        <div className="space-y-4">
          {STEPS.map(({ Icon, text }, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-white/8 border border-white/10 flex items-center justify-center shrink-0">
                <Icon className="text-brand-red text-sm"/>
              </div>
              <span className="text-white/60 text-xs">{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom: trust line */}
      <p className="text-white/25 text-[11px]">
        Trusted by athletes across Texas &mdash; expanding nationwide.
      </p>
    </aside>

    {/* ── Right form panel ─────────────────────────────────────────────── */}
    <div className="flex-1 flex flex-col min-h-screen">

      {/* Mobile header */}
      <header className="lg:hidden flex items-center justify-between px-5 h-14 border-b border-gray-100 shrink-0">
        <Link to="/" className="flex items-center gap-2">
          <img src="/1.svg" alt="Akture" className="h-8 w-auto object-contain" draggable={false}/>
        </Link>
        <Link to="/"
          className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors">
          <FaArrowLeft className="text-[10px]"/> Back
        </Link>
      </header>

      {/* Form area */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:py-16">
        <div className="w-full max-w-md">
          {/* Desktop back link */}
          <Link to="/"
            className="hidden lg:inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-700 transition-colors mb-8">
            <FaArrowLeft className="text-[10px]"/> Back to home
          </Link>
          {children}
        </div>
      </div>

      {/* Bottom bar */}
      <footer className="px-6 py-4 text-center text-[10px] text-gray-400 border-t border-gray-100 shrink-0">
        <span>&copy; {new Date().getFullYear()} Akture.&ensp;</span>
        <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy</Link>
        <span className="mx-2 opacity-40">·</span>
        <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
      </footer>
    </div>
  </div>
);

AuthLayout.propTypes = {
  children:    PropTypes.node.isRequired,
  headerProps: PropTypes.object,
  logoSrc:     PropTypes.string,
};

export default AuthLayout;
